import { tw } from "@careinfo/tailwind";
import { useForm } from "react-hook-form";
import APIS from "../../../constant/EndPoint";
import { toast } from "react-toastify";
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useSearchParams } from "react-router-dom";
import useGetHook from "../../../customHooks/useGetHook";
import { useQueryClient } from "@tanstack/react-query";
import { deliveryformValidation } from "../../../utils/validationSchemas";
import { postApiFormData } from "../../../helpers/AxiosInstance";
import TextField from "../../Reusable/TextInput";
import PhoneNumberField from "../../Reusable/NumField";
import LocalCombobox from "../../Reusable/LocalCombobox";
import DateInput from "../../Reusable/DateInput";
import MultiImageField from "../../Reusable/MultiImageField";
import SignaturePad from "../SignaturePad";
import Button from "../../Reusable/Button";

const DeliveryForm = (props: any) => {
  const { setIsOpen, deliveredStatus } = props;
  const [isLoading, setIsLoading] = useState(false);
  const IdType = [
    { name: "Driving Licence", id: "driving license" },
    { name: "National Identity", id: "national identity card" },
    { name: "Citizenship", id: "citizenship" },
    { name: "Passport", id: "passport" },
  ];

  const {
    control,
    register,
    setValue,
    clearErrors,
    handleSubmit,
    getValues,
    setError,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(deliveryformValidation),
  });

  const queryClient = useQueryClient();
  const [searchParams, setSearchParms] = useSearchParams();
  const selectedOrder = searchParams.get("selectedItems")?.split(",");
  const selectedTrackingNumber = searchParams.get("selectedWayBillNumber");

  const { data, isLoading: isDataLoading } = useGetHook({
    url: selectedTrackingNumber
      ? APIS.ORDER
      : `${APIS.ORDER}${selectedOrder?.[0]}/`,
    queryKey: [`order-${selectedOrder?.[0]}-${selectedTrackingNumber}`],
    params: {
      ...(selectedTrackingNumber
        ? {
            search: selectedTrackingNumber,
          }
        : null),
    },
  });

  const handleDelivered = async (formValues: any, setError: any) => {
    setIsLoading(true);
    const deliveredUrl = APIS.DELIVERY;

    const deliveredFormData = new FormData();
    deliveredFormData.append("full_name", formValues.full_name);
    deliveredFormData.append("primary_contact", formValues.primary_contact);
    deliveredFormData.append("identity", formValues.identity.name);
    deliveredFormData.append("identity_number", formValues.identity_number);
    deliveredFormData.append("remarks", formValues.remarks || "");
    if (formValues.identity_image) {
      deliveredFormData.append("identity_image", formValues.identity_image);
    }
    if (formValues.signature) {
      deliveredFormData.append("signature", formValues.signature);
    }
    formValues?.extra_data
      ? deliveredFormData.append(
          "extra_data",
          JSON.stringify(formValues?.extra_data)
        )
      : null;
    deliveredFormData.append(
      "tracking_number",
      selectedTrackingNumber ?? data?.tracking_number
    );
    try {
      const deliveredRes = await postApiFormData({
        url: deliveredUrl,
        formData: deliveredFormData,
        setError,
      });

      if (deliveredRes?.status === 200) {
        toast.success("Delivery submitted successfully!");
        searchParams.delete("selectedWayBillNumber");
        setSearchParms(searchParams);
        queryClient.invalidateQueries({
          queryKey: ["orders"],
          refetchType: "active",
        });
        setIsOpen(false);
      } else {
        toast.error(deliveredRes?.data?.message ?? "Something went wrong");
      }
    } catch (err) {
      console.error("Delivery submission failed:", err);
      toast.error("Something went wrong while submitting the delivery.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit((data) => handleDelivered(data, setError))}>
        <div className={tw`flex flex-col items-center gap-y-2`}>
          <div className={tw`w-full grid grid-cols-2 gap-x-2`}>
            <TextField
              label={"Full Name"}
              register={register("full_name")}
              errors={errors?.full_name}
            />
            <PhoneNumberField
              control={control}
              name={"primary_contact"}
              errors={errors?.primary_contact}
              label={"Mobile number"}
            />
            <LocalCombobox
              label={"Identity Type"}
              data={IdType}
              errors={errors?.identity}
              setValue={setValue}
              name={"identity"}
              register={register("identity")}
              clearErrors={clearErrors}
              value={getValues("identity")}
            />
            <TextField
              label={"Identity Number"}
              register={register("identity_number")}
              errors={errors?.identity_number}
            />
            <DateInput
              type="datetime-local"
              label={"Delivered Date"}
              register={register(`extra_data.date`)}
              min={data?.created}
              errors={errors?.extra_data}
            />
          </div>
          <MultiImageField name="identity_image" control={control} />
          <div className={tw`w-full `}>
            <label>
              <span>Remarks</span>
              <textarea
                rows={4}
                {...register("remarks")}
                className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
              />
            </label>
            {errors?.remarks && (
              <p className={tw`text-red-500 text-xs my-1`}>
                {errors?.remarks?.message}
              </p>
            )}
          </div>
          <SignaturePad
            setValue={setValue}
            getValues={getValues}
            clearErrors={clearErrors}
            register={register}
            errors={errors?.signature}
            name="signature"
          />
        </div>
        <div>
          <Button
            label={"Submit"}
            isLoading={isLoading}
            disabled={
              selectedTrackingNumber
                ? data?.[0]?.delivery_status.name === deliveredStatus?.label
                : data?.delivery_status?.id === deliveredStatus?.id
            }
          />
        </div>
      </form>
    </>
  );
};

export default DeliveryForm;
